import { SUBTYPES_BY_CATEGORY, type PropertyCategory, type PropertySubtype } from './property-taxonomy.schema';
import { COMMERCIAL_AREA_CODES, RESIDENTIAL_AREA_CODES } from '../property/area-split';
import {
  AREA_CODES,
  COMMERCIAL_AMENITY_CODES,
  type AreaCode,
  type CommercialAmenityCode,
} from '../property/properties.schemas';

/**
 * Which fields each Gewerbe-Untertyp asks for (IMM-788.5). Rendering only —
 * storage is never affected, and a hidden field keeps its value.
 *
 * ONE source, deliberately: the capture form, the detail page and the Exposé
 * ([IMM-220](https://linear.app/hlre/issue/IMM-220)) all read this map, so they
 * cannot drift into three inspections of the same rule (BL-082).
 */
export type CommercialFieldKey = AreaCode | CommercialAmenityCode;

/** Derived from the taxonomy so it cannot drift. */
export type CommercialSubtype = (typeof SUBTYPES_BY_CATEGORY.COMMERCIAL)[number];

/** Asked for by every Gewerbe-Untertyp: the headline area, the yield measure, parking and the room count. */
const COMMERCIAL_BASELINE = [
  'USABLE_AREA',
  'LETTABLE_AREA',
  'PARKING_SPACES',
  'ROOMS',
] as const satisfies ReadonlyArray<CommercialFieldKey>;

/**
 * Every key the map has an opinion about. DERIVED, never hand-listed (BL-083):
 * a code added to either source joins SPECIAL_PURPOSE's row for free.
 */
export const ALL_COMMERCIAL_FIELD_KEYS = [
  'USABLE_AREA',
  'ROOMS',
  ...COMMERCIAL_AREA_CODES,
  'MIN_DIVISIBLE_AREA',
  ...COMMERCIAL_AMENITY_CODES,
] as const satisfies ReadonlyArray<CommercialFieldKey>;

export const COMMERCIAL_FIELD_VISIBILITY = {
  OFFICE_PRACTICE: [...COMMERCIAL_BASELINE, 'OFFICE_AREA', 'MIN_DIVISIBLE_AREA', 'GOODS_LIFT'],
  RETAIL: [...COMMERCIAL_BASELINE, 'RETAIL_AREA', 'SHOP_AREA', 'MIN_DIVISIBLE_AREA', 'SHOP_WINDOW_LENGTH'],
  HOSPITALITY: [...COMMERCIAL_BASELINE, 'GASTRONOMY_AREA', 'NUMBER_OF_SEATS'],
  HOTEL: [...COMMERCIAL_BASELINE, 'GASTRONOMY_AREA', 'NUMBER_OF_SEATS', 'NUMBER_OF_BEDS'],
  INDUSTRIAL: [
    ...COMMERCIAL_BASELINE,
    'STORAGE_AREA',
    'HALL_AREA',
    'OFFICE_AREA',
    'MIN_DIVISIBLE_AREA',
    'HALL_HEIGHT',
    'FLOOR_LOAD',
    'LOADING_RAMP',
    'GOODS_LIFT',
    'GOODS_LIFT_LOAD',
    'CRANE_RUNWAY_LOAD',
    'CONNECTED_LOAD',
  ],
  SPECIAL_PURPOSE: ALL_COMMERCIAL_FIELD_KEYS,
} as const satisfies Record<CommercialSubtype, ReadonlyArray<CommercialFieldKey>>;

/** A commercial object with no subtype asks for everything — nothing is known to be irrelevant. */
export function visibleCommercialFields(subtype: PropertySubtype | null): ReadonlySet<CommercialFieldKey> {
  if (subtype === null || !(subtype in COMMERCIAL_FIELD_VISIBILITY)) return new Set(ALL_COMMERCIAL_FIELD_KEYS);

  return new Set(COMMERCIAL_FIELD_VISIBILITY[subtype as CommercialSubtype]);
}

const GOVERNED: ReadonlySet<string> = new Set(ALL_COMMERCIAL_FIELD_KEYS);
const RESIDENTIAL_ONLY: ReadonlySet<string> = new Set(RESIDENTIAL_AREA_CODES);
const COMMERCIAL_ONLY: ReadonlySet<string> = new Set(COMMERCIAL_AREA_CODES);

/**
 * What the areas editor offers. `ownCode` is ALWAYS kept, so a stored value stays
 * clearable on its own row — hidden is not deleted, and not unreachable either.
 */
export function offeredAreaCodes(
  category: PropertyCategory,
  subtype: PropertySubtype | null,
  ownCode: AreaCode | '',
): ReadonlyArray<AreaCode> {
  if (category !== 'COMMERCIAL') {
    // IMM-788.2, unchanged: off Gewerbe the commercial codes are not offered.
    return AREA_CODES.filter((code) => !COMMERCIAL_ONLY.has(code) || code === ownCode);
  }

  const visible = visibleCommercialFields(subtype);

  return AREA_CODES.filter(
    (code) => code === ownCode || (!RESIDENTIAL_ONLY.has(code) && (!GOVERNED.has(code) || visible.has(code))),
  );
}

/** What the amenities editor's commercial group offers. Same `ownCode` rule. */
export function offeredCommercialAmenityCodes(
  subtype: PropertySubtype | null,
  ownCode: string,
): ReadonlyArray<CommercialAmenityCode> {
  const visible = visibleCommercialFields(subtype);

  return COMMERCIAL_AMENITY_CODES.filter((code) => code === ownCode || visible.has(code));
}
